import React, { useEffect, useMemo, useState } from 'react';
import { Text } from '@asigna/kit';
import { getCreatedPermissions } from 'api';
import { Notification, SkeletonAsset } from 'components';
import { useActivateSafeFromUrlParam } from 'hooks';
import useAddress from 'hooks/use-address';
import useNetwork from 'hooks/use-network';
import useSafe from 'hooks/use-safe';
import { TransactionStatus, usePermissionList } from 'store';

import { Tab } from '../../ui-kit';

import { getHistoryTransactions, getQueueTransactions } from './util/txUtils';
import { History, Messages, Queue } from './components';

const Tabs = {
  QUEUE: 'Queue',
  HISTORY: 'History',
  MESSAGES: 'Signature request',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
type Tabs = (typeof Tabs)[keyof typeof Tabs];

export const ActivityPage: React.FC = () => {
  useActivateSafeFromUrlParam();
  const [selectedTab, setSelectedTab] = useState<Tabs>(Tabs.QUEUE);

  const { transactions, safe } = useSafe();
  const [network] = useNetwork();
  const walletAccount = useAddress();
  const { permissionList, setPermissionList, setIsLoading } = usePermissionList();

  const fetchPermissions = async () => {
    if (!Boolean(safe.address)) return;
    setIsLoading(true);
    getCreatedPermissions(safe.address, `stx:${network}`)
      .then((res) => {
        setPermissionList(res);
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    fetchPermissions().then();
  }, [safe.address]);

  const hasNotifications = useMemo(
    () =>
      transactions?.some((x) =>
        [TransactionStatus.QUEUED, TransactionStatus.EXECUTION].includes(x.status)
      ),
    [transactions]
  );

  const hasMessagesToSign = useMemo(
    () =>
      permissionList.some((signingMessage) => {
        if (!signingMessage.signatures || !signingMessage.signatures.length) {
          return true;
        }

        return !signingMessage.signatures.some((message) => message.userAddress === walletAccount);
      }),
    [permissionList, walletAccount]
  );

  // TODO add isLoading to tx atom
  const isLoading = safe.loading;

  useEffect(() => {
    fetchPermissions().then();
  }, []);

  const historyTransactions = useMemo(() => getHistoryTransactions(transactions), [transactions]);

  const queuedTransactions = useMemo(() => {
    return getQueueTransactions(transactions);
  }, [transactions]);

  const queuedTransactionsNonceCountList = useMemo(() => {
    return Array.from(
      queuedTransactions.reduce((map, queuedTransaction) => {
        const currentNonce = queuedTransaction.nonce;
        const previousNonceCount = map.get(queuedTransaction.nonce);

        return map.set(currentNonce, (previousNonceCount || 0) + 1);
      }, new Map<number, number>()),
      ([nonce, count]) => ({ nonce, count })
    );
  }, [queuedTransactions]);

  return (
    <div className="flex w-full sm:pr-24 h-screen py-16">
      <div className="flex flex-col rounded-[16px] bg-black-1 w-full relative py-16 sm:py-32 overflow-y-scroll">
        <Text message="Transactions" color="white" size="r24" className="mb-24 ml-16 sm:ml-32" />
        {/* {(queuedTransactions.length > 0 ||
          historyTransactions.length > 0 ||
          permissionList.length > 0) && ( */}
        <div className="flex gap-8 overflow-x-auto min-h-40 mb-[24px] mx-0 sm:mx-0 px-16 sm:px-32 pb-4 sm:pb-0 no-scrollbar">
          {Object.values(Tabs).map((tab) => (
            <Tab
              id={tab}
              key={tab}
              isActive={tab === selectedTab}
              onClick={() => setSelectedTab(tab)}
            >
              <>
                <span>{tab}</span>
                {tab === 'Queue' && hasNotifications && <Notification />}
                {tab === 'Signature request' && hasMessagesToSign && <Notification />}
              </>
            </Tab>
          ))}
        </div>

        <div className="mb-48 sm:mb-16">
          {isLoading ? (
            <SkeletonAsset mode={0} />
          ) : (
            <>
              {selectedTab === Tabs.QUEUE && (
                <Queue
                  queuedTransactions={queuedTransactions}
                  queuedTransactionsNonceCountList={queuedTransactionsNonceCountList}
                />
              )}
              {selectedTab === Tabs.HISTORY && (
                <History transactionsHistory={historyTransactions} />
              )}
              {selectedTab === Tabs.MESSAGES && <Messages />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
